import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X } from "lucide-react";
import { useCart } from "../context/CartContext";

const links = [
  { to: "/shop", label: "Collections" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const { count, setOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMobile(false), [location]);

  return (
    <header
      className="fixed top-0 inset-x-0 z-50"
      style={{
        transition: "background-color 0.4s ease, backdrop-filter 0.4s ease, box-shadow 0.4s ease",
        backgroundColor: scrolled ? "rgba(250,248,245,0.82)" : "transparent",
        backdropFilter: scrolled ? "blur(16px)" : "none",
        boxShadow: scrolled ? "0 1px 0 rgba(44,76,59,0.08)" : "none",
      }}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex flex-col leading-none group">
          <span className="font-serif text-2xl md:text-[1.7rem] font-semibold text-forest tracking-tight">
            Serendipitous Sips
          </span>
          <span className="text-[0.6rem] uppercase tracking-[0.35em] text-forest-soft ml-0.5">
            &amp; Wellness · Racine, WI
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l, i) => (
            <Link
              key={i}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className="text-sm uppercase tracking-[0.15em] text-forest relative group"
              style={{ transition: "color 0.3s ease" }}
            >
              {l.label}
              <span
                className="absolute -bottom-1 left-0 h-px bg-terracotta w-0 group-hover:w-full"
                style={{ transition: "width 0.35s ease" }}
              />
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <button
            onClick={() => setOpen(true)}
            data-testid="cart-button"
            className="relative p-2 text-forest hover:text-terracotta"
            style={{ transition: "color 0.3s ease" }}
            aria-label="Open cart"
          >
            <ShoppingBag className="w-5 h-5" />
            {count > 0 && (
              <span
                data-testid="cart-count"
                className="absolute -top-0.5 -right-0.5 bg-terracotta text-cream text-[0.6rem] w-4 h-4 rounded-full flex items-center justify-center font-semibold"
              >
                {count}
              </span>
            )}
          </button>
          <button
            className="md:hidden p-2 text-forest"
            onClick={() => setMobile((m) => !m)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {mobile ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {mobile && (
        <div className="md:hidden bg-cream/95 backdrop-blur-lg border-t border-black/5 px-6 py-6 flex flex-col gap-5">
          {links.map((l, i) => (
            <Link
              key={i}
              to={l.to}
              data-testid={`mobile-nav-${l.label.toLowerCase()}`}
              className="text-lg font-serif text-forest"
            >
              {l.label}
            </Link>
          ))}
        </div>
      )}
    </header>
  );
}
